// --------------------------
// IMPORTS
// --------------------------
import {
  Row,
  Col,
  DatePicker,
  Button,
  message,
  Typography,
  Modal,
  Badge,
  Card,
  List,
  Tooltip,
  Radio,
  Skeleton,
  Checkbox,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useRef, useMemo } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { SlideshowLightbox } from "lightbox.js-react";

import dayjs from "dayjs";
import CustomLoader from "../../utils/CustomLoader";
import useCurrentPath, {
  decryptData,
  draggerProps,
  getProjectCodeLabel,
  getRateByDate,
} from "../../utils/Constants";
import media from "../../media";
import {
  getListOfExpenseDetailThunk,
  updateListOfExpenseThunk,
} from "../../store/thunks/listOfExpensesThunk";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import Dragger from "antd/es/upload/Dragger";
import {
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getCurrencyListThunk,
  getProjectCodeListThunk,
  getUserListThunk,
  getUserListWithDetailThunk,
  getCurrencyListDetailThunk,
  getLocationListThunk,
  getDiemListThunk,
  getVendorListThunk,
} from "../../store/thunks/masterDataThunk";
import { getBlockedDateThunk } from "../../store/thunks/addNewExpenseThunk";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ExpenseListingDetail({
  detailPageRenderSource,
  isVisible,
  setIsVisible,
  recordKey,
  onCloseReset,
}) {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const { RangePicker } = DatePicker;
  const location = useLocation();
  const navigate = useNavigate();
  const tableTotalRef = useRef(0);
  const lastTotalRef = useRef("0.00");

  const id = recordKey;

  // --------------------------
  // REDUX STATE
  // --------------------------
  let storeRole = useSelector((state) => state.auth.role);
  const decryptedEmpID = useSelector((state) => state.auth.empID);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);
  const storeAcknowledgement = storeUserInfo?.user?.canSendExpenseRequest;
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [travelMediaItem, setTravelMediaItem] = useState(null);
  const [isFrontendTotalActive, setIsFrontendTotalActive] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [totalReimbursement, setTotalReimbursement] = useState(null);
  const [currencyRates, setCurrencyRates] = useState({});
  const [currenciesInfo, setCurrenciesInfo] = useState({});
  const [blockedDate, setBlockedDate] = useState(null);
  const [mainSelectionForModeOfPayment, setMainSelectionForModeOfPayment] =
    useState(null);
  const [creditCardOption, setCreditCardOption] = useState("");
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersDetailList, setUsersDetailList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [expenseDetail, setExpenseDetail] = useState({});
  const [natureOfExpenseSelectedValue, setNatureOfExpenseSelectedValue] =
    useState();
  const [errorMsg, setErrorMsg] = useState({ expensesDetails: [] }),
    [errors, setErrors] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [paymentPaidOn, setPaymentPaidOn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [mediaItem, setMediaItem] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTravelInputsChanged, setIsTravelInputsChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [natureOfExpense, setNatureOfExpense] = useState([
    { label: "Already Paid - Filling", value: "Already Paid - filling" },
    { label: "Reimbursement", value: "Reimbursement" },
    { label: "Travel Settlement", value: "Travel Settlement" },
    { label: "Vendor Bill", value: "Vendor Bill" },
  ]);
  const [showAdvanceAmount, setShowAdvanceAmount] = useState(false);
  const [vendors, setVendors] = useState([]);

  const [rows, setRows] = useState([
    {
      date: "",
      description: "",
      departmentID: "",
      expenseCodeID: "",
      currencyID: "",
      cost: "",
      mediaInfo: [],
    },
  ]);

  const [rechargeOpts, setRechargeOpts] = useState([
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ]);

  const [locations, setLocations] = useState([]);
  const [diems, setDiems] = useState([]);
  const [advanceAmountError, setAdvanceAmountError] = useState("");
  const [travelErrors, setTravelErrors] = useState({
    dateRange: "",
    location: "",
    numberOfDays: "",
    perDiem: "",
    exchangeRate: "",
    totalForeignAmount: "",
    totalINRAmount: "",
  });
  const [advanceErrors, setAdvanceErrors] = useState({
    amount: "",
    media: "",
  });
  const [resetTravelSettlementState, setResetTravelSettlementState] =
    useState(false);

  const finalModeOfPayment =
    mainSelectionForModeOfPayment === "Credit Card"
      ? `Credit Card: ${creditCardOption}`
      : mainSelectionForModeOfPayment;

  const isSentBackEditable =
    expenseDetail?.empID === decryptedEmpID &&
    expenseDetail?.status ===
      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase();

  const isTravelCurrencyLocked =
    isSentBackEditable && natureOfExpenseSelectedValue === "Travel Settlement";

  const prev = expenseDetail.natureOfExpense; // DB / saved
  const current = natureOfExpenseSelectedValue; // UI
  // --------------------------
  // API CALLS
  // --------------------------

  const expCal = () => {
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    dispatch(getBlockedDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response?.expCalRes) {
          response.expCalRes?.blockedOn &&
          response.expCalRes?.status === process.env.REACT_APP_BLOCK_STATUS
            ? setBlockedDate(response.expCalRes)
            : setBlockedDate(null);
        }
      });
  };

  const calculateTotalReimbursement = ({
    rows,
    expenseDetail,
    natureOfExpenseSelectedValue,
  }) => {
    // Repeater total
    const repeaterTotal = rows.reduce(
      (sum, r) => sum + Number(r.costInINR || 0),
      0,
    );

    //  Travel settlement INR (from backend)
    const travelINR = Number(
      expenseDetail?.travelSettlement?.[0]?.totalINRAmount || 0,
    );

    if (natureOfExpenseSelectedValue === "Travel Settlement") {
      return repeaterTotal.toFixed(2);
    }

    return (repeaterTotal - travelINR).toFixed(2);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  useEffect(() => {
    if (!expenseDetail?._id) return;

    const backendTotal = Number(expenseDetail?.totalReimbursement || 0).toFixed(
      2,
    );
    
    lastTotalRef.current = backendTotal;
  }, [expenseDetail?._id]);

  useEffect(() => {
    expCal();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        detailPageRenderSource && setShowLoader(true);
        const [
          detailResponse,
          userList,
          currencies,
          projectCodes,
          expenseCodes,
          departmentRes,
          locationsRes,
          usersRes,
          diems,
          vendorsRes,
        ] = await Promise.all([
          dispatch(
            getListOfExpenseDetailThunk({ route: currentPath, id }),
          ).unwrap(),
          dispatch(getUserListThunk()).unwrap(),
          dispatch(getCurrencyListThunk()).unwrap(),
          dispatch(getProjectCodeListThunk()).unwrap(),
          dispatch(getExpenseCodeListThunk()).unwrap(),
          dispatch(getDepartmentListThunk()).unwrap(),
          dispatch(getLocationListThunk()).unwrap(),
          dispatch(getUserListWithDetailThunk()).unwrap(),
          dispatch(getDiemListThunk()).unwrap(),
          dispatch(getVendorListThunk()).unwrap(),
        ]);

        const departmentId = Array.isArray(detailResponse?.departmentID)
          ? detailResponse?.departmentID?.[0]?._id
          : detailResponse?.departmentID;

        const deptInfo = departmentRes.find(
          (d) => String(d.value) === String(departmentId),
        );

        if (deptInfo?.label) {
          const [name = "", code = ""] = deptInfo.label.split(" - ");
          detailResponse.departmentID = `${name} - ${code}`;
        }
        setExpenseDetail(detailResponse);
        setNatureOfExpenseSelectedValue(detailResponse?.natureOfExpense);
        setShowAdvanceAmount(
          detailResponse?.travelSettlement?.[0]?.advanceAmount ? true : false,
        );
        setTravelMediaItem(
          detailResponse?.travelSettlement?.[0]?.travelSettleMediaInfo
            .length === 1
            ? "savedTravelMedia"
            : null,
        );        
        setRows(
          detailResponse?.expensesDetails?.map((d) => ({
            date: d.date || "",
            description: d.description || "",
            departmentID: d.departmentID || "",
            expenseCodeID: d.expenseCodeID || null,
            currencyID: d.currencyID || "",
            cost: d.cost || "",
            mediaInfo: d.mediaInfo || [],
            costInINR: Number(d.costInINR || 0),
          })) || [],
        );

        setUsers(userList?.response?.filter((u) => u.level2Approver) || []);
        setAllCurrencies(currencies?.currencyDropdown || []);
        setCurrencyRates(currencies?.currencyRates || []);
        setCurrenciesInfo(currencies.list || []);
        setExpenseCodes(expenseCodes || []);
        setProjectCodes(projectCodes || []);
        setDepartments(departmentRes || []);
        setLocations(locationsRes || []);
        setUsersDetailList(usersRes || []);
        setDiems(diems || []);
        setVendors(vendorsRes || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setShowLoader(false);
      }
    };
    fetchAll();
  }, [dispatch, currentPath, id]);

  useEffect(() => {
    const rowsTotal = rows.reduce((sum, r) => {
      try {
        const cost = Number(r.cost || 0);
        if (!cost) return sum;

        const currencyId =
          r.currencyID?._id || r.currencyID?.value || r.currencyID;

        const date =
          typeof r.date === "string" ? r.date : r.date?.format?.("YYYY-MM-DD");

        // If currency/date missing → treat as INR
        if (!currencyId || !date) {
          return sum + cost; //  DO NOT count yet
        }

        const matched = getRateByDate(currencyRates, currencyId, date);
        const rate = matched?.rate || 1;

        return sum + cost * rate;
      } catch (e) {
        console.error("Row calculation failed:", e, r);
        return sum;
      }
    }, 0);

    const isTravelSettlement =
      natureOfExpenseSelectedValue === "Travel Settlement";

    const travelINR = isTravelSettlement
      ? Number(expenseDetail?.travelSettlement?.[0]?.totalINRAmount || 0)
      : 0;

    const advance = isTravelSettlement
      ? Number(expenseDetail?.travelSettlement?.[0]?.advanceAmount || 0)
      : 0;

    const finalTotal = !showAdvanceAmount?(rowsTotal + travelINR).toFixed(2):(rowsTotal + travelINR - advance).toFixed(2);

    setTotalReimbursement(finalTotal);
  }, [
    rows,
    natureOfExpenseSelectedValue,
    expenseDetail?.travelSettlement?.[0]?.totalINRAmount,
    expenseDetail?.travelSettlement?.[0]?.advanceAmount,
    showAdvanceAmount,
    currencyRates,
  ]);

  useEffect(() => {
    if (!expenseDetail?.natureOfExpense) return;

    // Switching TO Travel Settlement
    setResetTravelSettlementState(false);
    if (prev !== "Travel Settlement" && current === "Travel Settlement") {
      setResetTravelSettlementState(true);      
    }    
  }, [natureOfExpenseSelectedValue, expenseDetail?.natureOfExpense]);

  // --------------------------
  // HANDLERS
  // --------------------------

  const handleTravelMediaChange = (file, onSuccess) => {
    if (!file) return;

    const maxSingleFileSize = 4 * 1024 * 1024;
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];

    if (file.size > maxSingleFileSize) {
      message.error("File exceeds 4MB.");
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const media = [
        {
          src: e.target.result,
          name: file.name,
          type: file.type,
        },
      ];

      setExpenseDetail((prev) => ({
        ...prev,
        travelSettlement: [
          {
            ...prev.travelSettlement?.[0],
            travelSettleMediaInfo: media,
          },
        ],
      }));      

      setTravelMediaItem("freshUpload");
      onSuccess("ok");
    };

    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  function getMinDate(dateStr) {
    const date = new Date(dateStr); // e.g., "2025-05-22"
    const year = date.getFullYear();
    const month = date.getMonth(); // Current month (0-indexed)

    // Set to 0th day of current month to get last day of previous month
    return new Date(year, month, 0); // e.g., 30 April 2025
  }

  const getUserDiem = (user, diems) => {
    if (!user?.diemId || !Array.isArray(diems)) return null;

    return diems.find((d) => d._id === user.diemId) || null;
  };
  const buildPerDiemRates = (user, diems) => {
    const diem = getUserDiem(user, diems);
    if (!diem) return {};

    return {
      INR: Number(diem.inr) || 0,
      AUD: Number(diem.aud) || 0,
      USD: Number(diem.usd) || 0,
      GBP: Number(diem.gbp) || 0,
      CAD: Number(diem.cad) || 0,
      EU: Number(diem.eu) || 0,
    };
  };

  const handleChange = (index, field, value) => {
    setIsFrontendTotalActive(true);
    setHasUserInteracted(true);
    // Create a copy of the mediaInfo array for the respective detail

    let updatedRows = [...rows];

    if (!updatedRows[index]) {
      message.error(`Row at index ${index} does not exist!`);
      return; // Exit early if the row at the given index does not exist
    }
    if (
      field === "departmentID" ||
      field === "expenseCodeID" ||
      field === "currencyID" ||
      field === "recharge"
    ) {
      updatedRows[index][field] = value?.value;
    } else {
      updatedRows[index][field] = value;
    }

    const currentRow = updatedRows[index];
    const { date, currencyID, cost } = currentRow;
    const costValue = parseFloat(cost || 0);

    //  Only process if both date and currency are selected
    if (date && currencyID) {
      const matched = getRateByDate(currencyRates, currencyID, date);
      const rate = matched?.rate || 1;
      const rateDateTime = matched?.effectiveDateTime || null;

      updatedRows[index].currencyRate = rate;      
      updatedRows[index].costInINR = Number((costValue * rate).toFixed(2));
    } else {
      // If not all values selected, just default costInINR = cost
      const rate = currencyRates[currencyID]?.[0]?.rate || 1;
      updatedRows[index].costInINR = Number((costValue * rate).toFixed(2));
    }

    // Recalculate total reimbursement
    const total = updatedRows.reduce((sum, r) => {
      const val = parseFloat(r.costInINR || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    setRows(updatedRows);

    // Update the mediaInfo in the specific detail
    const updatedDetails = (expenseDetail?.expensesDetails || [])?.map(
      (item, idx) => {
        if (idx === index) {
          return {
            ...item,
            [field]: updatedRows[index][field],
          };
        }
        return item;
      },
    );
    // Update the state or parent component's state with the updated expenseDetail
    setExpenseDetail((prevState) => ({
      ...prevState,
      expensesDetails: updatedDetails,
    }));
  };

  const handleImageClick = (filePath) => {
    const index = images.findIndex((img) => img === filePath);
    if (index !== -1) {
      setPhotoIndex(index);
      setIsOpen(true);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onFinish = (e) => {
    e.preventDefault();
    if (
      storeRole === process.env.REACT_APP_ADROLE &&
      expenseDetail?.status ===
        process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      paymentStatus === false
    ) {
      //!approversApproved
      message.error("Payment Status is Mandatory");
      setErrorMsg({
        ...errorMsg,
        paymentStatus: "Payment Status is Mandatory",
      });
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      paymentStatus === true &&
      paymentPaidOn === null &&
      mainSelectionForModeOfPayment === null
    ) {
      message.error(
        "Payment Paid On date and Mode of Payment both are Mandatory",
      );
      setErrorMsg({
        ...errorMsg,
        paymentPaidOn: "Payment Paid On date is Mandatory",
        modeOfPayment: "Mode of Payment is Mandatory",
      });
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      paymentStatus === true &&
      paymentPaidOn === null &&
      mainSelectionForModeOfPayment !== null
    ) {
      message.error("Payment Paid On date is Mandatory");
      setErrorMsg({
        ...errorMsg,
        paymentPaidOn: "Payment Paid On date is Mandatory",
      });
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      paymentStatus === true &&
      paymentPaidOn !== null &&
      mainSelectionForModeOfPayment === "Credit Card" &&
      creditCardOption === ""
    ) {
      message.error("Please select Credit Card option");
      setErrorMsg({
        ...errorMsg,
        modeOfPayment: "Please select Credit Card option",
      });
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      paymentStatus === true &&
      mainSelectionForModeOfPayment === null &&
      paymentPaidOn !== null
    ) {
      message.error("Mode of Payment is Mandatory");
      setErrorMsg({
        ...errorMsg,
        modeOfPayment: "Mode of Payment is Mandatory",
      });
    } else if (errors?.fileSizeExceed === "single") {
      message.error("File size exceeds 5 MB.");
    } else if (errors?.fileSizeExceed === "total") {
      message.error("The total size of all files must not exceed 10 MB.");
    } else if (errors?.fileCountExceed) {
      message.error(
        "You cannot only upload one attachment so upload accordingly.",
      );
    } else if (
      natureOfExpenseSelectedValue !== "Travel Settlement" &&
      totalReimbursement.indexOf("-") !== -1
    ) {
      message.error(
        totalReimbursement +
          " amount is with you. If you have any concern then please contact with Finance Department!!",
      );
    } else {
      setIsModalVisible(true);
    }
  };

  const validateAdvanceAmount = () => {    
    if (!showAdvanceAmount) {
      setAdvanceErrors({ amount: "", media: "" });
      return true;
    }

    const advanceAmount = expenseDetail?.travelSettlement?.[0]?.advanceAmount;

    const media =
      expenseDetail?.travelSettlement?.[0]?.travelSettleMediaInfo.length;    

    let isValid = true;

    // Amount validation
    if (!advanceAmount) {
      setAdvanceErrors((prev) => ({
        ...prev,
        amount: "Please enter advance amount",
      }));
      isValid = false;
    } else {
      setAdvanceErrors((prev) => ({
        ...prev,
        amount: "",
      }));
    }

    // Media validation
    if (media === 0 || travelMediaItem === null) {
      setAdvanceErrors((prev) => ({
        ...prev,
        media: "Please upload advance amount document",
      }));
      isValid = false;
    } else {
      setAdvanceErrors((prev) => ({
        ...prev,
        media: "",
      }));
    }

    return isValid;
  };

  const validateTravelSettlement = () => {
    let errors = {
      dateRange: "",
      location: "",
      totalINRAmount: "",
      numberOfDays: "",
      perDiem: "",
      exchangeRate: "",
      totalForeignAmount: "",
      totalINRAmount: "",
    };

    if (natureOfExpenseSelectedValue === "Travel Settlement") {
      const fromDate = expenseDetail?.travelSettlement?.[0]?.fromDate;
      const toDate = expenseDetail?.travelSettlement?.[0]?.toDate;
      const location = expenseDetail?.travelSettlement?.[0]?.location;
      const numberOfDays = expenseDetail?.travelSettlement?.[0]?.numberOfDays;
      const perDiem = expenseDetail?.travelSettlement?.[0]?.perDiem;
      const totalForeignAmount =
        expenseDetail?.travelSettlement?.[0]?.totalForeignAmount;
      const totalINRAmount =
        expenseDetail?.travelSettlement?.[0]?.totalINRAmount;

      // Date validation
      if (!fromDate || !toDate) {
        errors.dateRange = "Please select travel period";
      }

      // Location validation
      if (!location) {
        errors.location = "Please select location";
      }

      // numberOfDays validation
      if (!numberOfDays) {
        errors.numberOfDays = "No. of Days";
      }

      // perDiem validation
      if (!perDiem) {
        errors.perDiem = "No. of Days";
      }

      // totalForeignAmount validation
      if (!totalForeignAmount) {
        errors.totalForeignAmount = "Total";
      }

      // totalINRAmount validation
      if (!totalINRAmount) {
        errors.totalINRAmount = "Total (INR)";
      }
    }

    setTravelErrors(errors);

    // return true if no errors
    return (
      !errors.dateRange &&
      !errors.location &&
      !errors.totalINRAmount &&
      !errors.numberOfDays &&
      !errors.perDiem &&
      !errors.totalForeignAmount &&
      !errors.totalINRAmount
    );
  };
  const handleResubmitClick = async () => {
    // Travel Settlement Validation
    if (!validateTravelSettlement()) {
      message.error("Please complete Travel Settlement details");
      return;
    }

    // Advance Amount Validation
    if (!validateAdvanceAmount()) {
      message.error("Please complete Advance Amount details");
      return;
    }

    // All validations passed
    setIsModalVisible(true);
  };
  const handleOk = () => {
    setIsModalVisible(false);

    setShowLoader(true);
    setErrors({ fileSizeExceed: "", fileCountExceed: false });

    dispatch(
      updateListOfExpenseThunk({
        role: storeRole,
        lIUId: storeLIUId,
        empName: expenseDetail?.empName,
        empID: parseInt(expenseDetail?.empID),
        departmentID: expenseDetail?.departmentID?._id,
        projectCodeID: expenseDetail?.projectCodeID?._id,
        expensePeriodFromDate: expenseDetail?.expensePeriodFromDate,
        expensePeriodToDate: expenseDetail?.expensePeriodToDate,
        natureOfExpense: natureOfExpenseSelectedValue,
        businessPurpose: (
        ((prev !== "Travel Settlement" && current !== "Travel Settlement") || (prev === "Travel Settlement" && current !== "Travel Settlement"))) ? expenseDetail?.businessPurpose || null: null,
        expensesDetails: rows?.[0]?.cost
          ? rows
          : expenseDetail?.expensesDetails,
        paymentPaidOn,
        modeOfPayment: finalModeOfPayment,
        totalReimbursement,
        ackMessage: expenseDetail?.ackMessage || null,
        historySource: "backend",
        historyAction:
          paymentPaidOn != null
            ? `Admin updated payment status: ${paymentPaidOn}`
            : expenseDetail?.status ===
                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
              ? "sentback-resubmitted"
              : "update",
        historyUserID: storeLIUId,
        status: paymentPaidOn
          ? expenseDetail?.status
          : expenseDetail?.status ===
              process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
            ? process.env.REACT_APP_RESUBMITTED_STATUS.toUpperCase()
            : expenseDetail?.status,
        route: currentPath,
        travelSettlement:
          (prev !== "Travel Settlement" && current === "Travel Settlement") ||
          (prev === "Travel Settlement" && current === "Travel Settlement")
            ? {
                uploadType:travelMediaItem==="freshUpload"?true:false,
                fromDate: expenseDetail?.travelSettlement?.[0]?.fromDate,
                toDate: expenseDetail?.travelSettlement?.[0]?.toDate,
                location: typeof expenseDetail?.travelSettlement?.[0]?.location === "string"?expenseDetail?.travelSettlement?.[0]?.location:expenseDetail?.travelSettlement?.[0]?.location?.value,
                numberOfDays:
                  expenseDetail?.travelSettlement?.[0]?.numberOfDays,
                perDiem: expenseDetail?.travelSettlement?.[0]?.perDiem,
                exchangeRate:
                  expenseDetail?.travelSettlement?.[0]?.exchangeRate,
                totalForeignAmount:
                  expenseDetail?.travelSettlement?.[0]?.totalForeignAmount,
                totalINRAmount:
                  expenseDetail?.travelSettlement?.[0]?.totalINRAmount,
                advanceAmount:!showAdvanceAmount?null:expenseDetail?.travelSettlement?.[0]?.advanceAmount || null,
                travelSettleMediaInfo:
                  !showAdvanceAmount?[]:expenseDetail?.travelSettlement?.[0]?.travelSettleMediaInfo ||
                  [],
              }
            : null,
        id,
      }),
    )
      .then((response) => {
        message.success(
          response && response?.paymentPaidOn !== null
            ? "Expense updated successfully."
            : "Expense resubmitted successfully.",
        );
        setExpenseDetail({ ...expenseDetail });
        setRows([
          {
            date: "",
            description: "",
            departmentID: "",
            expenseCodeID: "",
            currencyID: "",
            cost: "",
            mediaInfo: [],
          },
        ]);
        setShowLoader(false);

        // Notify parent to refresh list
        if (onCloseReset) onCloseReset();

        navigate(`/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}`);
        setIsVisible(false);
      })
      .catch((error) => {
        setShowLoader(false);
      });
  };

  //  Travel Perdiem calculation start here
  const PER_DIEM_RATES = useMemo(() => {
    return buildPerDiemRates(storeUserInfo?.user, diems);
  }, [storeUserInfo?.user?.diemId, diems]);

  const getNumberOfDays = (from, to) => {
    if (!from || !to) return 0;

    const start = moment(from, "YYYY-MM-DD");
    const end = moment(to, "YYYY-MM-DD");

    return end.diff(start, "days") + 1;
  };
  const getExchangeRateToINR = (
    currencyCode,
    currenciesInfo,
    effectiveDate,
  ) => {
    if (!currencyCode) return 1;

    const currency = currenciesInfo.find(
      (c) => String(c.code) === String(currencyCode),
    );

    if (!currency?.currencyRateHistory?.length) return 1;

    const sorted = [...currency.currencyRateHistory].sort(
      (a, b) => new Date(b.effectiveDateTime) - new Date(a.effectiveDateTime),
    );

    const applicable =
      sorted.find(
        (r) => new Date(r.effectiveDateTime) <= new Date(effectiveDate),
      ) || sorted[0];

    return Number(applicable?.rate) || 1;
  };

  useEffect(() => {
    const fromDate = expenseDetail?.travelSettlement?.[0]?.fromDate;
    const toDate = expenseDetail?.travelSettlement?.[0]?.toDate;

    // ---- Get location safely ----
    const locationFromState = expenseDetail?.travelSettlement?.[0]?.location;

    const location = locationFromState;    

    if (!fromDate || !toDate || !location) return;

    // ---- Extract label safely ----
    const locationLabel = location.label || location; // works for both object or string
    
    // ---- Calculate Days ----
    const days = moment(toDate).diff(moment(fromDate), "days") + 1;

    // ---- Get Currency ----
    const currency = locationLabel.split("-")[1]?.trim();

    if (!currency) return;

    // ---- Per Diem & Exchange Rate ----
    const perDiem = PER_DIEM_RATES[currency] || 0;

    const exchangeRate = getExchangeRateToINR(
      currency,
      currenciesInfo,
      fromDate,
    );

    const totalForeignAmount = days * perDiem;
    const totalINRAmount = totalForeignAmount * exchangeRate;

    // Find currency object
    const currencyObj = currenciesInfo?.find((c) => c.code === currency);
    
    setExpenseDetail((prev) => ({
      ...prev,
      travelSettlement: [
        {
          ...(prev.travelSettlement?.[0] || {}),
          location: locationFromState,
          numberOfDays: days,
          perDiem,
          exchangeRate,
          totalForeignAmount: Number(totalForeignAmount).toFixed(2),
          totalINRAmount: Number(totalINRAmount).toFixed(2),
        },
      ],
      expensesDetails: prev.expensesDetails?.map((row) => {
        if (row.currencyID === currencyObj?._id) return row;
        

        return {
          ...row,
          currencyID: currencyObj?._id,
        };
      }),
    }));

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        currencyID: currencyObj?._id,
      })),
    );

    setTravelErrors((prev) => ({
      ...prev,
      dateRange: "",
      location: "",
      numberOfDays: "",
      perDiem: "",
      exchangeRate: "",
      totalForeignAmount: "",
      totalINRAmount: "",
    }));
  }, [
    expenseDetail?.travelSettlement?.[0]?.fromDate,
    expenseDetail?.travelSettlement?.[0]?.toDate,
    expenseDetail?.travelSettlement?.[0]?.location,
  ]);
  //  Travel Perdiem calculation close here

  // --------------------------
  // UTILITY FUNCTIONS
  // --------------------------

  const getCurrencyCodeFromLocation = (location) => {
    
    if (!location || !location?.label) return null;
    if (location?.label) {    
      return location.label.split("-")[1]?.trim();
    } else {
      return location.split("-")[1]?.trim();
    } // USD, AUD
  };

  const normalizeCurrencyValue = (currencyOption) =>
    currencyOption?.value ?? null;
  const effectiveLocation = expenseDetail?.travelSettlement?.[0]?.location;

  const normalizedLocation = useMemo(() => {
    if (!effectiveLocation) return null;

    // If it's already an object from Select, use its value
    if (typeof effectiveLocation === "object" && effectiveLocation?.value) {
      return effectiveLocation.value;
    }

    // Otherwise it's already an ID (string)
    return effectiveLocation;
  }, [effectiveLocation]);

  const locationObj = useMemo(() => {
    if (!normalizedLocation || !Array.isArray(locations)) return null;

    return locations.find(
      (item) => String(item.value) === String(normalizedLocation),
    );
  }, [normalizedLocation, locations]);

  // Detect currency from location
  const locationCurrency = useMemo(() => {
    if (!locationObj) return null;
    return getCurrencyCodeFromLocation(locationObj);
  }, [locationObj]);

  // Disable dates after the current date
  const disableFutureDates = (current) => {
    const startDate = moment(current).subtract(1, "month").startOf("day");

    // Calculate the end date for the next financial year
    const currentYear = moment().year();
    const nextFinancialYearEnd = moment()
      .month(2)
      .date(31)
      .year(
        currentYear + (moment().isAfter(moment().month(2).date(31)) ? 1 : 0),
      )
      .endOf("day");

    // Disable dates outside the range two months ago from today to next financial year's 31st March
    return (
      current &&
      (current.isBefore(startDate) || current.isAfter(moment().endOf("day")))
    );
  };

  const disableFutureDatesForPaymentPaidOn = (current) => {
    const startDate = moment(current).startOf("day");

    // Calculate the end date for the next financial year
    const currentYear = moment().year();
    const nextFinancialYearEnd = moment()
      .month(2)
      .date(31)
      .year(
        currentYear + (moment().isAfter(moment().month(2).date(31)) ? 1 : 0),
      )
      .endOf("day");

    // Disable dates outside the range two months ago from today to next financial year's 31st March
    return (
      current &&
      (current.isBefore(startDate) || current.isAfter(nextFinancialYearEnd))
    );
  };

  const images =
    expenseDetail?.expensesDetails?.flatMap((detail) =>
      detail?.mediaInfo
        ?.filter(
          (file) =>
            file?.savedFileType === "jpeg" ||
            file?.savedFileType === "jpg" ||
            file?.savedFileType === "png",
        )
        ?.map(
          (file) => process.env.REACT_APP_BACKEND + "/" + file?.savedFilepath,
        ),
    ) || [];

  const hasViewAccess =
    storeRole === process.env.REACT_APP_AROLE
      ? expenseDetail &&
        expenseDetail?.approvers?.filter((expenseValue) => {
          return expenseValue?.userID == storeLIUId;
        })
      : "";
  const approversStatusCount = expenseDetail?.approvers?.filter(
    (approver) =>
      approver.status === process.env.REACT_APP_APPROVED_STATUS.toUpperCase(),
  )?.length;

  useEffect(() => {
    // Only check conditions once the data is available , itself || manager || admin after manager approver
    if (storeRole && expenseDetail) {
      if (decryptedEmpID !== expenseDetail?.empID) {
        if (
          hasViewAccess &&
          hasViewAccess?.length === 0 &&
          storeUserInfo?.user?.canSendExpenseRequest !== false
        ) {
          setShouldRedirect(true);
        }
      }
      setIsLoading(false);
    }
  }, [storeRole, expenseDetail]);

  if (shouldRedirect) {
    return <Navigate to="/dashboard" />;
  }

  // Function to remove travel media
  const handleTravelMediaRemove = () => {
    Modal.confirm({
      title: "Confirm Removal",
      content: "Are you sure you want to remove this media item?",
      okText: "Yes",
      cancelText: "No",
      className: "revokeOrDeleteConfirmModal",
      okButtonProps: { className: "defaultbutton" },
      cancelButtonProps: { className: "defaultWhiteBtn" },
      onOk: () => {
        setTravelMediaItem(null);
        setExpenseDetail((prev) => ({
          ...prev,
          travelSettlement: [
            {
              ...prev.travelSettlement?.[0],
              travelSettleMediaInfo: [],
            },
          ],
        }));
      },
    });
  };

  // Function to remove media
  const handleMediaRemove = (detail, index) => {
    // Show confirmation modal
    Modal.confirm({
      title: "Confirm Removal",
      content: "Are you sure you want to remove this media item?",
      okText: "Yes",
      cancelText: "No",
      className: "revokeOrDeleteConfirmModal",
      okButtonProps: {
        className: "defaultbutton",
      },
      cancelButtonProps: {
        className: "defaultWhiteBtn",
      },
      onOk: () => {
        // Create a copy of the mediaInfo array for the respective detail
        let updatedMediaInfo = [...detail.mediaInfo];
        // Remove the selected file from the array (e.g., removing the first item for simplicity)
        updatedMediaInfo.splice(0, 1); // You can adjust the index based on your logic

        // Check if mediaInfo is empty after removal and set it explicitly to an empty array
        if (updatedMediaInfo?.length === 0) {
          updatedMediaInfo = []; // Set to empty array if no files are left
          //updateRowFiles(index, fileSources, updatedRows);
        }
        // Update the mediaInfo in the specific detail
        const updatedDetails = expenseDetail?.expensesDetails?.map(
          (item, idx) => {
            if (idx === index) {
              return {
                ...item,
                mediaInfo: updatedMediaInfo,
              };
            }
            return item;
          },
        );

        // Update the state or parent component's state with the updated expenseDetail
        setExpenseDetail((prevState) => ({
          ...prevState,
          expensesDetails: updatedDetails,
        }));
        setRows((prevRows) =>
          prevRows?.map((row, idx) => {
            if (idx === index) {
              return {
                ...row,
                mediaInfo: updatedMediaInfo, // Update mediaInfo for the specific row
              };
            }
            return row; // Keep other rows unchanged
          }),
        );
        setMediaItem((prevState) => ({
          ...prevState,
          ["item_" + index]: "removed",
        }));
      },
      onCancel: () => {
        // Optionally, handle cancellation logic here
        console.log("Removal cancelled");
      },
    });
  };

  const handleFileChange = (index, fileOrEvent, onSuccess) => {
    // Handle both Dragger (file object) and Browse File (event)
    const files = fileOrEvent?.target?.files || [fileOrEvent]; // auto-detect source

    if (files.length > 1) {
      setErrors({ fileCountExceed: true });
      message.error("Only one file can be uploaded.");
      return;
    }

    setErrors({ fileCountExceed: false });

    const maxSingleFileSize = 4 * 1024 * 1024; // 4MB
    const file = files[0];
    const updatedRows = [...rows];

    // File size check
    if (file.size > maxSingleFileSize) {
      setErrors({ fileSizeExceed: "single" });
      message.error(`File "${file.name}" exceeds 4MB.`);
      return;
    }

    // File type check
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed!");
      return;
    }

    const reader = new FileReader();
    const fileSources = [];

    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    reader.onload = (e) => {
      let fileData;
      if (file.type.startsWith("image/")) {
        fileData = e.target.result;
      } else {
        const base64Data = arrayBufferToBase64(e.target.result);
        fileData = `data:${file.type};base64,${base64Data}`;
      }

      fileSources.push({
        src: fileData,
        name: file.name,
        type: file.type,
      });

      updateRowFiles(index, fileSources, updatedRows);
      onSuccess("ok");
    };

    // Read file accordingly
    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const updateRowFiles = (index, fileSources, updatedRows) => {
    if (!updatedRows[index].mediaInfo) {
      updatedRows[index].mediaInfo = [];
    }
    updatedRows[index].mediaInfo = fileSources;
    setRows(updatedRows);
  };

  const getStatusBadge = (status, reason) => {
    switch (status) {
      case "APPROVED":
        return (
          <Badge
            status="success"
            text="Approved"
            icon={<CheckCircleOutlined />}
            className="approved-badge"
          />
        );
      case "PENDING":
        return (
          <Badge
            status="warning"
            text="Pending Approval: Request Sent"
            icon={<ClockCircleOutlined />}
            className="pending-badge"
          />
        );
      case "CANCELED":
        return (
          <Badge
            status="error"
            text={
              <span>
                <strong>Canceled</strong>: {reason}
              </span>
            }
            icon={<CloseCircleOutlined />}
            className="canceled-badge"
          />
        );
      case "SENTBACK":
        return (
          <Badge
            status="processing"
            text={
              <span>
                <strong>Sent Back</strong>: {reason}
              </span>
            }
            icon={<RedoOutlined />}
            className="sentBack-badge"
          />
        );
      default:
        return (
          <Badge
            status="default"
            text="Awaiting previous Approver's Approval"
            icon={<ExclamationCircleOutlined />}
          />
        );
    }
  };

  // Assign intermediate variables for conditions
  const isNoResponseStatus =
    expenseDetail?.status ===
      process.env.REACT_APP_NORESPONSE_STATUS.toUpperCase().replace(" ", "") &&
    expenseDetail?.isNoResponse;

  const isHistoryNoResponseStatus =
    expenseDetail?.history &&
    expenseDetail?.history[
      expenseDetail?.history?.length - 1
    ]?.status.toUpperCase() ===
      process.env.REACT_APP_NORESPONSE_STATUS.toUpperCase().replace(" ", "");

  const isStoreRoleAdmin = storeRole === process.env.REACT_APP_ADROLE;

  const isSentBackStatus =
    expenseDetail?.status ===
    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase();

  const isCurrentUser = expenseDetail?.empID === decryptedEmpID;

  // Now combine the conditions logically
  const shouldShowResubmitButton =
    (isNoResponseStatus && isHistoryNoResponseStatus && isStoreRoleAdmin) ||
    (isCurrentUser && isSentBackStatus);

  // Assigning intermediate variables for conditions
  const isPendingWithNoAcknowledgment =
    approversStatusCount === expenseDetail?.approvers?.length &&
    expenseDetail?.status ===
      process.env.REACT_APP_PENDING_STATUS.toUpperCase() &&
    expenseDetail?.seekAcknowledgment?.[0]?.sentAt === null;
  const isSentBack =
    expenseDetail?.history &&
    expenseDetail?.history[expenseDetail?.history?.length - 1].status ===
      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase();
  const isApprovedWithNoPayment =
    expenseDetail?.status ===
      process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
    expenseDetail?.paymentPaidOn === null;

  const isStoreRoleAdminWithPending =
    storeRole === process.env.REACT_APP_ADROLE && isApprovedWithNoPayment;

  const isStoreRoleARoleWithAcknowledgment =
    storeRole === process.env.REACT_APP_AROLE &&
    storeAcknowledgement === false &&
    expenseDetail?.seekAcknowledgment?.[0]?.sentAt !== null &&
    expenseDetail?.seekAcknowledgment?.[0]?.ackMessage === null;

  // Now use these variables in the condition
  const shouldShowUpdateButton =
    isStoreRoleAdminWithPending || isStoreRoleARoleWithAcknowledgment;

  const handleMainChange = (e) => {
    const value = e.target.value;
    if (value) {
      setMainSelectionForModeOfPayment(value);
    }

    if (value !== "Credit Card") {
      setCreditCardOption(""); // reset if switching away
    }
  };

  const handleCreditCardOptionChange = (e) => {
    setCreditCardOption(e.target.value);
  };  

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      {isVisible && (
        <div className="addExpensePopupForm">
          <div
            className={`addExpensePopupFormWraper ${
              isVisible ? "addExpensePopupFormWraper-visible" : ""
            }`}
          >
            {showLoader ? (
              <CustomLoader showLoader={showLoader} />
            ) : (
              <>
                {/* {detailPageRenderSource && <CustomLoader showLoader={showLoader} />} */}
                <div className="expenseDetailTitle contentBottomPadding">
                  <h3 className="text-dark font-size-lg">Expense Details</h3>
                  <button
                    onClick={() => {
                      setIsVisible(false);
                      if (onCloseReset) {
                        onCloseReset(); // ⬅ Parent will refresh list & clear search
                      }
                      !detailPageRenderSource && navigate(location.pathname);
                    }}
                    className="expenseDetailTitleClose"
                  >
                    <img src={media.popupCross.svg} alt="close" />
                  </button>
                </div>
                <div className="customExpenseLabelForm">
                  <form
                    layout="vertical"
                    autoComplete="off"
                    className="form-validation-start"
                    onSubmit={onFinish}
                  >
                    <div style={{ padding: "0 9px" }}>
                      <Row className="addMainWrap" gutter={[18, 24]}>
                        <Col xs={24} md={12} xl={12}>
                          <div className="form-row-wrap">
                            <label name="empID">Employee ID</label>

                            <input
                              placeholder="Id"
                              className="form-control rounded-md"
                              defaultValue={expenseDetail?.empID}
                              disabled
                            />
                          </div>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <div className="form-row-wrap">
                            <label name="empName">Employee Name</label>

                            <input
                              defaultValue={expenseDetail?.empName}
                              disabled
                              placeholder="Employee Name"
                              className="form-control rounded-md"
                            />
                          </div>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <div className="form-row-wrap">
                            <label name="departmentID">Department </label>

                            <input
                              placeholder="Department"
                              className="form-control rounded-md"
                              disabled
                              value={expenseDetail?.departmentID}
                            />
                          </div>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <div className="form-row-wrap">
                            <label htmlFor="expensePeriodFromDate">
                              Expense Period: (From - To)
                              <span className="mandatory-symbol">*</span>
                            </label>

                            <RangePicker
                              // disabledDate={disableFutureDates}
                              className="form-control rounded-md d-inline-flex"
                              format="DD-MM-YYYY"
                              value={[
                                expenseDetail?.expensePeriodFromDate
                                  ? moment(expenseDetail?.expensePeriodFromDate)
                                  : null,
                                expenseDetail?.expensePeriodToDate
                                  ? moment(expenseDetail?.expensePeriodToDate)
                                  : null,
                              ]}
                              disabled
                            />
                          </div>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <div className="form-row-wrap">
                            <label name="projectCodeID">
                              Project Code
                              <span className="mandatory-symbol">*</span>
                            </label>

                            <input
                              className="form-control rounded-md"
                              disabled
                              value={getProjectCodeLabel(
                                expenseDetail?.projectCodeID,
                                projectCodes,
                              )}
                            />
                          </div>
                        </Col>
                        {expenseDetail?.natureOfExpense && (
                          <Col
                            xs={24}
                            md={12}
                            xl={12}
                            id={
                              expenseDetail?.empID === decryptedEmpID &&
                              expenseDetail?.status ===
                                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                ? ""
                                : "loe-disb"
                            }
                          >
                            <div className="form-row-wrap">
                              <label name="natureOfExpense">
                                Nature of Expense
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <Select
                                isDisabled={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? false
                                    : true
                                }
                                className={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? "rounded-md"
                                    : "select-isDisabled rounded-md"
                                }
                                placeholder="Select nature of expense"
                                defaultValue={{
                                  label: `${expenseDetail?.natureOfExpense}`,

                                  value: expenseDetail?.natureOfExpense,
                                }}
                                onChange={(item) => {
                                  const newNature = item.value;

                                  if (newNature !== "Travel Settlement") {
                                    setIsTravelInputsChanged(false);
                                  }

                                  setIsFrontendTotalActive(true);
                                  setNatureOfExpenseSelectedValue(newNature);
                                  const total = calculateTotalReimbursement({
                                    rows,
                                    expenseDetail,
                                    natureOfExpenseSelectedValue: newNature,
                                  });
                                  
                                }}
                                options={natureOfExpense}
                              />
                            </div>
                          </Col>
                        )}
                        {natureOfExpenseSelectedValue !==
                          "Travel Settlement" && (
                          <Col sm={24}>
                            <div className="form-row-wrap">
                              <label name="businessPurpose">
                                Vendor Name
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Select
                                className="h-45px lh-1Pt5 rounded-md"
                                allowClear
                                options={vendors}
                                value={expenseDetail?.businessPurpose}
                                onChange={(value) => {
                                  setExpenseDetail((prev) => ({
                                    ...prev,
                                    businessPurpose: value,
                                  }));
                                }}
                                disabled={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? false
                                    : true
                                }
                              />
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                    {/* Travel Expense Section start here */}
                    {natureOfExpenseSelectedValue === "Travel Settlement" && (
                      <div className="travelSettlement viewTravelSettlement">
                        <h3>Travel Settlement</h3>
                        <Row className="travelSettlementRow" gutter={[18, 24]}>
                          <Col xs={24} md={12} xl={12}>
                            <div className="form-row-wrap">
                              <label name="fromDate">
                                Travel Settlement Period: (From - To)
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <RangePicker
                                format="DD-MM-YYYY"
                                // disabledDate={disableFutureDates}
                                disabledDate={(current) => {
                                  const minDate = new Date(
                                    blockedDate?.blockedOn,
                                  );
                                  return (
                                    current &&
                                    current.toDate() <= getMinDate(minDate)
                                  );
                                }}
                                disabled={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? false
                                    : true
                                }
                                className="form-control rounded-md d-inline-flex"
                                defaultValue={[
                                  expenseDetail?.travelSettlement?.[0]?.fromDate
                                    ? dayjs(
                                        expenseDetail.travelSettlement[0]
                                          .fromDate,
                                        "YYYY-MM-DD",
                                      )
                                    : null,
                                  expenseDetail?.travelSettlement?.[0]?.toDate
                                    ? dayjs(
                                        expenseDetail.travelSettlement[0]
                                          .toDate,
                                        "YYYY-MM-DD",
                                      )
                                    : null,
                                ]}
                                allowClear={true}
                                onChange={(date, dates) => {
                                  const formattedDates =
                                    dates &&
                                    dates?.map((date) =>
                                      moment(date, "DD-MM-YYYY").format(
                                        "YYYY-MM-DD",
                                      ),
                                    );

                                  if (
                                    formattedDates?.[0] &&
                                    formattedDates?.[1]
                                  ) {
                                    setExpenseDetail((prev) => ({
                                      ...prev,
                                      travelSettlement: [
                                        {
                                          ...prev.travelSettlement?.[0],
                                          fromDate: formattedDates[0],
                                          toDate: formattedDates[1],
                                        },
                                      ],
                                    }));

                                    setTravelErrors((prev) => ({
                                      ...prev,
                                      travelSettlement: "",
                                    }));
                                  }
                                  setIsTravelInputsChanged(true);
                                  setHasUserInteracted(true);
                                }}
                              />
                              {travelErrors.dateRange && (
                                <small className="text-danger">
                                  {travelErrors.dateRange}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={12} xl={12}>
                            <div className="form-row-wrap">
                              <label name="locationID">
                                Location{" "}
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Select
                                isDisabled={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? false
                                    : true
                                }
                                className={
                                  expenseDetail?.empID === decryptedEmpID &&
                                  expenseDetail?.status ===
                                    process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                    ? "rounded-md"
                                    : "select-isDisabled rounded-md"
                                }
                                placeholder="Select Location"
                                defaultValue={locations.find(
                                  (d) =>
                                    String(d.value) ===
                                    String(
                                      expenseDetail?.travelSettlement?.[0]
                                        ?.location,
                                    ),
                                )}
                                onChange={(value) => {                                  
                                  setExpenseDetail((prev) => ({
                                    ...prev,
                                    travelSettlement: [
                                      {
                                        ...prev.travelSettlement?.[0],
                                        location: value,
                                      },
                                    ],
                                  }));

                                  setErrorMsg({
                                    ...errorMsg,
                                    location: "Location is required",
                                  });
                                  setIsTravelInputsChanged(true);
                                  //setIsTravelLocationChanged(true);
                                  setHasUserInteracted(true);
                                }}
                                options={locations}
                              />

                              {travelErrors.location && (
                                <small className="text-danger">
                                  {travelErrors.location}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={6}>
                            <div className="form-row-wrap travelFieldsReadOnly">
                              <label name="numberOfDays">
                                No. of Days
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <input
                                placeholder="No. of Days"
                                className="form-control rounded-md"
                                value={
                                  expenseDetail?.travelSettlement?.[0]
                                    ?.numberOfDays
                                }
                                disabled
                              />
                              {travelErrors.numberOfDays && (
                                <small className="text-danger">
                                  {travelErrors.numberOfDays}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={6}>
                            <div className="form-row-wrap travelFieldsReadOnly">
                              <label name="perDiem">
                                Per Diem
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <input
                                placeholder="Per Diem"
                                className="form-control rounded-md"
                                value={
                                  expenseDetail?.travelSettlement?.[0]?.perDiem
                                }
                                disabled
                              />
                              {travelErrors.perDiem && (
                                <small className="text-danger">
                                  {travelErrors.perDiem}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={6}>
                            <div className="form-row-wrap travelFieldsReadOnly">
                              <label name="total">
                                Total
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <input
                                placeholder="Total"
                                className="form-control rounded-md"
                                value={
                                  expenseDetail?.travelSettlement?.[0]
                                    ?.totalForeignAmount
                                }
                                disabled
                              />
                              {travelErrors.totalForeignAmount && (
                                <small className="text-danger">
                                  {travelErrors.totalForeignAmount}
                                </small>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={6}>
                            <div className="form-row-wrap travelFieldsReadOnly">
                              <label name="totalINRAmount">
                                Total (INR)
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <input
                                placeholder="Total (INR)"
                                className="form-control rounded-md"
                                value={
                                  expenseDetail?.travelSettlement?.[0]
                                    ?.totalINRAmount
                                }
                                disabled
                              />
                              {travelErrors.totalINRAmount && (
                                <small className="text-danger">
                                  {travelErrors.totalINRAmount}
                                </small>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {natureOfExpenseSelectedValue === "Travel Settlement" &&
                      locationCurrency &&
                      locationCurrency !== "INR" &&
                      (() => {
                        const hasExistingAdvance =
                          Number(
                            expenseDetail?.travelSettlement?.[0]
                              ?.advanceAmount || 0,
                          ) > 0;
                        return (
                          
                          <div className={expenseDetail?.status !==
                                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && !hasExistingAdvance?"":"travelSettlement"}>
                            {expenseDetail?.status !==
                                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && !hasExistingAdvance ? "" :<h3>Advance Amount:</h3>}
                            {expenseDetail?.empID === decryptedEmpID &&
                              expenseDetail?.status ===
                                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                                <Checkbox
                                  checked={
                                    showAdvanceAmount
                                  }
                                  onChange={(e) => {
                                    setShowAdvanceAmount(!showAdvanceAmount);                                    
                                  }}
                                >
                                  Any Advance Amount?
                                </Checkbox>
                              )}

                            {(showAdvanceAmount) && (
                              <Row gutter={[18, 24]} className="mt-8">
                                <Col xs={24} md={24} xl={24}>
                                  <div className="form-row-wrap">
                                    <label name="total">
                                      Advance Amount (INR)
                                    </label>

                                    <input
                                      type="number"
                                      disabled={
                                        expenseDetail?.empID ===
                                          decryptedEmpID &&
                                        expenseDetail?.status ===
                                          process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                          ? false
                                          : true
                                      }
                                      placeholder="Please enter the advance amount (INR)"
                                      className={`form-control rounded-md ${
                                        advanceAmountError ? "input-error" : ""
                                      }`}
                                      value={
                                        expenseDetail?.travelSettlement?.[0]
                                          ?.advanceAmount
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        // allow only numbers
                                        if (!/^\d*\.?\d*$/.test(value)) return;

                                        setExpenseDetail((prev) => ({
                                          ...prev,
                                          travelSettlement: [
                                            {
                                              ...prev?.travelSettlement?.[0],
                                              advanceAmount: value,
                                            },
                                          ],
                                        }));

                                        setIsTravelInputsChanged(true);
                                        validateAdvanceAmount(value);
                                      }}
                                    />
                                    {advanceErrors.amount && (
                                      <small className="text-danger">
                                        {advanceErrors.amount}
                                      </small>
                                    )}
                                  </div>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {travelMediaItem === "savedTravelMedia" &&
                                    expenseDetail?.travelSettlement?.[0]
                                      ?.travelSettleMediaInfo?.length > 0 &&
                                    expenseDetail?.travelSettlement?.[0]?.travelSettleMediaInfo?.map(
                                      (file, indx) => {
                                        const fileName = file?.savedFilepath
                                          ?.split(/[/\\]/)
                                          .pop();

                                        return (
                                          <div className="fileIcon" key={indx}>
                                            <small>Document</small>
                                            <div className="symbol-100 symbol-light h-65 float-left">
                                              {expenseDetail?.empID ===
                                                decryptedEmpID &&
                                                expenseDetail?.status ===
                                                  process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                                                  <i
                                                    className="ki ki-close upload_close"
                                                    onClick={() =>
                                                      handleTravelMediaRemove(
                                                        expenseDetail
                                                          ?.travelSettlement?.[0],
                                                      )
                                                    }
                                                  ></i>
                                                )}

                                              {file?.savedFileType === "jpeg" ||
                                              file?.savedFileType === "jpg" ||
                                              file?.savedFileType === "png" ? (
                                                <SlideshowLightbox>
                                                  <img
                                                    className="rounded"
                                                    src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                    alt="media"
                                                    width="100"
                                                    height="100"
                                                    style={{
                                                      cursor: "pointer",
                                                    }}
                                                    onClick={() =>
                                                      handleImageClick(
                                                        `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`,
                                                      )
                                                    }
                                                  />
                                                </SlideshowLightbox>
                                              ) : (
                                                <a
                                                  href={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  <img
                                                    src={media.pdfIcon.svg}
                                                    alt="File Icon"
                                                  />
                                                  <span>{fileName}</span>
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}

                                  {/* Upload Section */}
                                  {(travelMediaItem === null ||
                                    travelMediaItem === "freshUpload") &&
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                                      <div className="symbol-100 symbol-light h-65">
                                        <Dragger
                                          className="uploadDrag"
                                          multiple={false}
                                          customRequest={({
                                            file,
                                            onSuccess,
                                          }) =>
                                            handleTravelMediaChange(
                                              file,
                                              onSuccess,
                                            )
                                          }
                                        >
                                          <p className="ant-upload-drag-icon">
                                            <img src={media.upload.svg} />
                                          </p>
                                          <p className="ant-upload-text">
                                            Drag your file(s) to start uploading
                                          </p>
                                          <p className="ant-upload-hint">OR</p>
                                          <span className="uploadBtn">
                                            <input
                                              id="travelSettlementFile"
                                              type="file"
                                              accept=".png, .jpg, .jpeg, .pdf, .docx, .doc"
                                              // onChange={(event) =>
                                              //   handleFileChangeTravelSettlement(event)
                                              // }
                                              style={{ display: "none" }}
                                            />
                                            <img
                                              src={media.uploadIcon.svg}
                                              alt="Upload info"
                                              title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                            />{" "}
                                            Browse file
                                          </span>
                                          <p className="ant-upload-text">
                                            Only one file allowed (.jpg, .png,
                                            .pdf, max 4MB)
                                          </p>
                                        </Dragger>

                                        {/* {expenseDetail?.travelSettlement?.[0]
                                          ?.mediaInfo?.length > 0 ? (
                                          <Typography.Text>
                                            {
                                              expenseDetail
                                                ?.travelSettlement?.[0]
                                                ?.mediaInfo.length
                                            }{" "}
                                            file(s) attached
                                          </Typography.Text>
                                        ) : (
                                          <Typography.Text>
                                            Only one file can be uploaded.
                                            Allowed file types: .jpg, .png, max
                                            size: 4MB.
                                          </Typography.Text>
                                        )} */}
                                        {advanceErrors.media && (
                                          <small className="text-danger d-block mt-1">
                                            {advanceErrors.media}
                                          </small>
                                        )}
                                      </div>
                                    )}
                                </Col>
                              </Row>
                            )}
                          </div>
                        );
                      })()}
                    {/* Travel Expense Section close here */}
                    {/*Repeater Area*/}
                    <div className="repeter rounded-sm expenseAddRepeater">
                      <Row className="expenseListTitle" gutter={[24, 24]}>
                        <div className="addExpenseSubGroup">
                          <h3>Add Expense Details</h3>
                        </div>
                      </Row>

                      {expenseDetail?.expensesDetails &&
                        expenseDetail?.expensesDetails?.map((detail, index) => {
                          const canShowUploader =
                            expenseDetail?.empID === decryptedEmpID &&
                            expenseDetail?.status ===
                              process.env.REACT_APP_SENTBACK_STATUS?.toUpperCase() &&
                            (mediaItem?.[`item_${index}`] === "removed" ||
                              mediaItem?.[`item_${index}`] === "freshUpload" ||
                              !detail?.mediaInfo?.length);
                          const departmentId =
                            typeof detail?.departmentID === "object"
                              ? detail?.departmentID?._id
                              : detail?.departmentID;

                          const expenseCodeId =
                            typeof detail?.expenseCodeID === "object"
                              ? detail?.expenseCodeID?._id
                              : detail?.expenseCodeID;

                          const currencyId =
                            typeof detail?.currencyID === "object"
                              ? detail?.currencyID?._id
                              : detail?.currencyID;
                          const locationOption = locations.find(
                            (c) =>
                              c.value ===
                              expenseDetail?.travelSettlement?.[0]?.location,
                          );
                          const currencyCode =
                            locationOption?.label?.split("-")?.[1]?.trim() ||
                            null;
                          return (
                            <div className="expenseDetqailsRepeater">
                              <h3 className="text-dark font-size-lg expenseRepeaterDetailHeading">
                                {`Expense Detail #${index + 1}`}
                              </h3>
                              <Row
                                gutter={[18, 24]}
                                className="RepaetRow"
                                key={index}
                              >
                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Date"
                                      className="block mb-1 font-medium"
                                    >
                                      Date
                                      <span className="mandatory-symbol">
                                        *
                                      </span>
                                    </label>
                                    <DatePicker
                                      format="DD-MM-YYYY"
                                      // disabledDate={disableFutureDates}
                                      disabled={
                                        expenseDetail?.empID ===
                                          decryptedEmpID &&
                                        expenseDetail?.status ===
                                          process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                          ? false
                                          : true
                                      }
                                      className="form-control rounded-md d-inline-flex"
                                      defaultValue={[
                                        detail?.date
                                          ? dayjs(detail?.date, "YYYY-MM-DD")
                                          : null,
                                      ]}
                                      allowClear={true}
                                      onChange={(date) => {
                                        const formattedDate =
                                          date.format("YYYY-MM-DD");
                                        handleChange(
                                          index,
                                          "date",
                                          formattedDate,
                                        );

                                        const tempErrors = [
                                          ...errorMsg.expensesDetails,
                                        ];

                                        tempErrors[index] = {
                                          ...tempErrors[index],

                                          date: date ? "" : "Date is Mandatory",
                                        };

                                        setErrorMsg({
                                          ...errorMsg,
                                          expensesDetails: tempErrors,
                                        });
                                      }}
                                    />
                                  </div>
                                  <small className="text-danger">
                                    {errorMsg.expensesDetails[index]?.date}
                                  </small>
                                </Col>

                                <Col
                                  xs={24}
                                  md={12}
                                  xl={12}
                                  id={
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                      ? ""
                                      : "loe-disb"
                                  }
                                >
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Description"
                                      className="block mb-1 font-medium"
                                    >
                                      Description
                                      <span className="mandatory-symbol">
                                        *
                                      </span>
                                    </label>
                                    <input
                                      disabled={
                                        expenseDetail?.empID ===
                                          decryptedEmpID &&
                                        expenseDetail?.status ===
                                          process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                          ? false
                                          : true
                                      }
                                      placeholder="Description"
                                      className="form-control rounded-md text-capitalize"
                                      defaultValue={detail?.description}
                                      onChange={(e) => {
                                        handleChange(
                                          index,
                                          "description",
                                          e.target.value,
                                        );

                                        const tempErrors = [
                                          ...errorMsg.expensesDetails,
                                        ];
                                        tempErrors[index] = {
                                          ...tempErrors[index],
                                          description: e.target.value
                                            ? ""
                                            : "Description is mandatory",
                                        };
                                        setErrorMsg({
                                          ...errorMsg,
                                          expensesDetails: tempErrors,
                                        });
                                      }}
                                    />
                                  </div>
                                  <small className="text-danger">
                                    {
                                      errorMsg.expensesDetails[index]
                                        ?.description
                                    }
                                  </small>
                                </Col>

                                <Col
                                  xs={24}
                                  md={12}
                                  xl={12}
                                  id={
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                      ? ""
                                      : "loe-disb"
                                  }
                                >
                                  {departments?.length > 0 && (
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="Department"
                                        className="block mb-1 font-medium"
                                      >
                                        Department
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Select
                                        isDisabled={
                                          expenseDetail?.empID ===
                                            decryptedEmpID &&
                                          expenseDetail?.status ===
                                            process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                            ? false
                                            : true
                                        }
                                        className={
                                          expenseDetail?.empID ===
                                            decryptedEmpID &&
                                          expenseDetail?.status ===
                                            process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                            ? "rounded-md"
                                            : "select-isDisabled rounded-md"
                                        }
                                        placeholder="Select Department"
                                        defaultValue={departments.find(
                                          (d) =>
                                            String(d.value) ===
                                            String(departmentId),
                                        )}
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "departmentID",
                                            value,
                                          );
                                          const tempErrors = [
                                            ...errorMsg.expensesDetails,
                                          ];
                                          tempErrors[index] = {
                                            ...tempErrors[index],
                                            departmentID: value?.value
                                              ? ""
                                              : "Department is Mandatory",
                                          };
                                          setErrorMsg({
                                            ...errorMsg,
                                            expensesDetails: tempErrors,
                                          });
                                        }}
                                        options={departments}
                                      />

                                      <small className="text-danger">
                                        {
                                          errorMsg.expensesDetails[index]
                                            ?.departmentID
                                        }
                                      </small>
                                    </div>
                                  )}
                                </Col>

                                <Col
                                  xs={24}
                                  md={12}
                                  xl={12}
                                  id={
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                      ? ""
                                      : "loe-disb"
                                  }
                                >
                                  {expenseCodes?.length > 0 && (
                                    <div
                                      title={
                                        expenseCodes.find(
                                          (obj) => obj.value === expenseCodeId,
                                        )?.label || "Select Expense Code"
                                      }
                                    >
                                      <div className="form-row-wrap">
                                        <label
                                          htmlFor="Expense Code"
                                          className="block mb-1 font-medium"
                                        >
                                          Expense Code
                                          <span className="mandatory-symbol">
                                            *
                                          </span>
                                        </label>
                                        <Select
                                          isDisabled={
                                            expenseDetail?.empID ===
                                              decryptedEmpID &&
                                            expenseDetail?.status ===
                                              process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                              ? false
                                              : true
                                          }
                                          className={
                                            expenseDetail?.empID ===
                                              decryptedEmpID &&
                                            expenseDetail?.status ===
                                              process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                              ? "rounded-md"
                                              : "select-isDisabled rounded-md"
                                          }
                                          placeholder={"Select Expense Code"}
                                          defaultValue={expenseCodes.find(
                                            (obj) =>
                                              obj.value === expenseCodeId,
                                          )}
                                          onChange={(value) => {
                                            handleChange(
                                              index,
                                              "expenseCodeID",
                                              value,
                                            );
                                            const tempErrors = [
                                              ...errorMsg.expensesDetails,
                                            ];
                                            tempErrors[index] = {
                                              ...tempErrors[index],
                                              expenseCodeID: value?.value
                                                ? ""
                                                : "Expense Code is Mandatory",
                                            };
                                            setErrorMsg({
                                              ...errorMsg,
                                              expensesDetails: tempErrors,
                                            });
                                          }}
                                          options={expenseCodes}
                                        />
                                      </div>
                                      <small className="text-danger">
                                        {
                                          errorMsg.expensesDetails[index]
                                            ?.expenseCodeID
                                        }
                                      </small>
                                    </div>
                                  )}
                                </Col>

                                <Col
                                  xs={24}
                                  md={12}
                                  xl={12}
                                  id={
                                    !isSentBackEditable ||
                                    isTravelCurrencyLocked
                                      ? "loe-disb"
                                      : ""
                                  }
                                >
                                  {allCurrencies?.length > 0 && (
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="Currency"
                                        className="block mb-1 font-medium"
                                      >
                                        Currency
                                        <span className="mandatory-symbol">*</span>
                                      </label>

                                      <Select
                                        isDisabled={
                                          !isSentBackEditable ||
                                          isTravelCurrencyLocked
                                        }
                                        className={
                                          !isSentBackEditable ||
                                          isTravelCurrencyLocked
                                            ? "select-isDisabled rounded-md"
                                            : "rounded-md"
                                        }
                                        placeholder="Select a currency"
                                        
                                        value={
                                          (() => {
                                            const locationRaw =
                                              expenseDetail?.travelSettlement?.[0]?.location;
                                            
                                            if (!locationRaw || !allCurrencies?.length) return null;

                                            let currencyCode = null;

                                            // ================= LOCKED → FROM LOCATION LABEL =================
                                            if (isTravelCurrencyLocked && typeof locationRaw === "string") {
                                              if (!locations?.length) return null;

                                              const locationObj = locations.find(
                                                (l) => l.value === locationRaw
                                              );

                                              if (locationObj?.label) {
                                                currencyCode =
                                                  locationObj.label.split("-")[1]?.trim();
                                              }
                                            }

                                            // ================= FALLBACK OBJECT =================
                                            if (!currencyCode && typeof locationRaw === "object") {
                                              
                                              const text =
                                                locationRaw.label ||
                                                locationRaw.value;

                                              if (text?.includes("-")) {
                                                currencyCode =
                                                  text.split("-")[1]?.trim();
                                              }
                                            }


                                            // ================= CASE: currencyCode STILL NULL =================
                                            if (!currencyCode) {
                                              // 1️ Try old currencyId logic
                                              if (currencyId) {
                                                const byCurrencyId = allCurrencies.find(
                                                  (c) =>
                                                    String(c.value) === String(currencyId) ||
                                                    String(c._id) === String(currencyId)
                                                );

                                                if (byCurrencyId) return byCurrencyId;
                                              }

                                              // 2️ Try locationRaw as ID
                                              if (typeof locationRaw === "string") {
                                                const byLocationId = allCurrencies.find(
                                                  (c) =>
                                                    String(c.value) === String(locationRaw) ||
                                                    String(c._id) === String(locationRaw)
                                                );
                                                
                                                if (byLocationId) return byLocationId;
                                              }

                                              return null;
                                            }

                                            // ================= MATCH BY CODE =================
                                            const matched = allCurrencies.find((c) => {
                                              return (
                                                c.code === currencyCode ||
                                                c.currencyCode === currencyCode ||
                                                c.label?.includes(currencyCode) ||
                                                c.name?.includes(currencyCode)
                                              );
                                            });
                                            return matched || null;
                                          })()
                                        }
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "currencyID",
                                            value,
                                          );

                                          const tempErrors = [
                                            ...errorMsg.expensesDetails,
                                          ];
                                          tempErrors[index] = {
                                            ...tempErrors[index],
                                            currencyID: value?.value
                                              ? ""
                                              : "Currency is Mandatory",
                                          };

                                          setErrorMsg({
                                            ...errorMsg,
                                            expensesDetails: tempErrors,
                                          });
                                        }}
                                        options={
                                          isTravelCurrencyLocked
                                            ? []
                                            : allCurrencies
                                        }
                                      />

                                      <small className="text-danger">
                                        {
                                          errorMsg.expensesDetails[index]
                                            ?.currencyID
                                        }
                                      </small>
                                    </div>
                                  )}
                                </Col>

                                <Col
                                  xs={24}
                                  md={12}
                                  xl={12}
                                  id={
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                      ? ""
                                      : "loe-disb"
                                  }
                                >
                                  {rechargeOpts?.length > 0 && (
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="Recharge"
                                        className="block mb-1 font-medium"
                                      >
                                        Recharge
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>

                                      <Select
                                        isDisabled={
                                          expenseDetail?.empID ===
                                            decryptedEmpID &&
                                          expenseDetail?.status ===
                                            process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                            ? false
                                            : true
                                        }
                                        className={
                                          expenseDetail?.empID ===
                                            decryptedEmpID &&
                                          expenseDetail?.status ===
                                            process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                            ? "rounded-md"
                                            : "select-isDisabled rounded-md"
                                        }
                                        placeholder={
                                          detail?.recharge
                                            ? "Select a recharge"
                                            : "N/A"
                                        }
                                        defaultValue={
                                          detail?.recharge
                                            ? rechargeOpts.find(
                                                (opt) =>
                                                  opt.value ===
                                                  String(detail.recharge),
                                              )
                                            : rechargeOpts.find(
                                                (opt) => opt.value === "No",
                                              )
                                        }
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "recharge",
                                            value,
                                          );
                                          const tempErrors = [
                                            ...errorMsg.expensesDetails,
                                          ];
                                          tempErrors[index] = {
                                            ...tempErrors[index],
                                            recharge: value
                                              ? ""
                                              : "Recharge is Mandatory",
                                          };
                                          setErrorMsg({
                                            ...errorMsg,
                                            expensesDetails: tempErrors,
                                          });
                                        }}
                                        options={rechargeOpts}
                                      />
                                      <small className="text-danger">
                                        {
                                          errorMsg.expensesDetails[index]
                                            ?.recharge
                                        }
                                      </small>
                                    </div>
                                  )}
                                </Col>

                                <Col
                                  xs={24}
                                  md={24}
                                  xl={24}
                                  id={
                                    expenseDetail?.empID === decryptedEmpID &&
                                    expenseDetail?.status ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                      ? ""
                                      : "loe-disb"
                                  }
                                >
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Cost"
                                      className="block mb-1 font-medium"
                                    >
                                      Cost
                                      <span className="mandatory-symbol">
                                        *
                                      </span>
                                    </label>

                                    <input
                                      disabled={
                                        expenseDetail?.empID ===
                                          decryptedEmpID &&
                                        expenseDetail?.status ===
                                          process.env.REACT_APP_SENTBACK_STATUS.toUpperCase()
                                          ? false
                                          : true
                                      }
                                      placeholder="Cost"
                                      className="form-control rounded-md"
                                      defaultValue={detail?.cost}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        handleChange(index, "cost", value);

                                        // Regular expression to allow only numbers and decimals
                                        const regex = /^[0-9]*\.?[0-9]*$/;

                                        // Update the cost field only if it matches the regex
                                        if (regex.test(value) || value === "") {
                                          const tempErrors = [
                                            ...errorMsg.expensesDetails,
                                          ];

                                          tempErrors[index] = {
                                            ...tempErrors[index],

                                            cost: value
                                              ? ""
                                              : "Cost is Mandatory",
                                          };

                                          setErrorMsg({
                                            ...errorMsg,
                                            expensesDetails: tempErrors,
                                          });
                                        } else {
                                          // Set error message if invalid input

                                          const tempErrors = [
                                            ...errorMsg.expensesDetails,
                                          ];

                                          tempErrors[index] = {
                                            ...tempErrors[index],

                                            cost: "Only numbers and decimals are allowed",
                                          };

                                          setErrorMsg({
                                            ...errorMsg,

                                            expensesDetails: tempErrors,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <small className="text-danger">
                                    {errorMsg?.expensesDetails[index]?.cost}
                                  </small>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  {detail?.mediaInfo?.length > 0 &&
                                    mediaItem?.["item_" + index] ===
                                      undefined &&
                                    detail?.mediaInfo?.map((file, indx) => {
                                      const fileName = file?.savedFilepath
                                        ?.split(/[/\\]/)
                                        .pop();
                                      return (
                                        <div className="fileIcon" key={indx}>
                                          <small>Document</small>
                                          <div
                                            className="symbol-100 symbol-light h-65 float-left"
                                            key={indx}
                                          >
                                            {expenseDetail?.empID ===
                                              decryptedEmpID &&
                                              expenseDetail?.status ===
                                                process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                                                <i
                                                  className="ki ki-close upload_close"
                                                  onClick={() =>
                                                    handleMediaRemove(
                                                      detail,
                                                      index,
                                                    )
                                                  }
                                                ></i>
                                              )}

                                            {file?.savedFileType === "jpeg" ||
                                            file?.savedFileType === "jpg" ||
                                            file?.savedFileType === "png" ? (
                                              <SlideshowLightbox>
                                                <img
                                                  className="rounded"
                                                  src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                  alt="media"
                                                  width="100"
                                                  height="100"
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                    handleImageClick(
                                                      `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`,
                                                    )
                                                  }
                                                />
                                              </SlideshowLightbox>
                                            ) : (
                                              <a
                                                href={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                <img
                                                  src={media.pdfIcon.svg}
                                                  alt="File Icon"
                                                />
                                                <span>{fileName}</span>
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  {canShowUploader && (
                                    <div className="symbol-100 symbol-light h-65">
                                      <Dragger
                                        className="uploadDrag"
                                        customRequest={({ file, onSuccess }) =>
                                          handleFileChange(
                                            index,
                                            file,
                                            onSuccess,
                                          )
                                        }
                                      >
                                        <p className="ant-upload-drag-icon">
                                          <img src={media.upload.svg} />
                                        </p>
                                        <p className="ant-upload-text">
                                          Drag your file(s) to start uploading
                                        </p>
                                        <p className="ant-upload-hint">OR</p>
                                        <span className="uploadBtn">
                                          <input
                                            id={`fileInput${index}`}
                                            type="file"
                                            accept=".png, .jpg, .jpeg, .pdf, .docx, .doc"
                                            onChange={(event) =>
                                              handleFileChange(index, event)
                                            }
                                            style={{ display: "none" }}
                                          />
                                          <img
                                            src={media.uploadIcon.svg}
                                            alt="Upload info"
                                            title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                          />{" "}
                                          Browse file
                                        </span>
                                        <p className="ant-upload-text">
                                          Only one file allowed (.jpg, .png,
                                          .pdf, max 4MB)
                                        </p>
                                      </Dragger>
                                      {/* Typography section that synchronizes with mediaInfo */}
                                      {/* {detail?.mediaInfo &&
                                            detail?.mediaInfo.length > 0 ? (
                                              <Typography.Text>
                                                {detail?.mediaInfo.length}{" "}
                                                file(s) attached
                                              </Typography.Text>
                                            ) : (
                                              <Typography.Text>
                                                Only one file can be uploaded.
                                                Allowed file types: .jpg, .png,
                                                max size: 4MB.
                                              </Typography.Text>
                                            )} */}
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          );
                        })}
                    </div>
                    {/*Repeater Area*/}
                    <Row className="expenseListAprovers">
                      <Col className="approverCol" xs={24} md={12} xl={12}>
                        <Card title="Approvers Status" bordered={true}>
                          {(() => {
                            // Get unique approvers (latest per user)
                            const uniqueApprovers = Object.values(
                              (expenseDetail?.approvers || []).reduce(
                                (acc, curr) => {
                                  if (
                                    !acc[curr.userID] ||
                                    curr.dateTime > acc[curr.userID].dateTime
                                  ) {
                                    acc[curr.userID] = curr;
                                  }
                                  return acc;
                                },
                                {},
                              ),
                            );

                            if (!uniqueApprovers.length) return null;

                            return (
                              <List
                                itemLayout="horizontal"
                                dataSource={uniqueApprovers}
                                renderItem={(approver) => {
                                  const approverInfo = usersDetailList.find(
                                    (userItem) =>
                                      userItem.id === approver?.userID,
                                  );

                                  return (
                                    <List.Item key={approver.userID}>
                                      <List.Item.Meta
                                        title={
                                          <div className="spanApprovers">
                                            <span className="approvLabel">
                                              Approver Name:
                                            </span>

                                            <div className="imgTitle">
                                              {approverInfo?.profilePicUrl ? (
                                                <img
                                                  src={
                                                    process.env
                                                      .REACT_APP_BACKEND +
                                                    "/images/" +
                                                    approverInfo.profilePicUrl
                                                  }
                                                  alt="profile"
                                                />
                                              ) : (
                                                <span className="initialLetter">
                                                  {approverInfo?.empName
                                                    ?.charAt(0)
                                                    ?.toUpperCase() || "?"}
                                                </span>
                                              )}

                                              <span>
                                                <h4>
                                                  {approverInfo?.empName ||
                                                    "N/A"}
                                                </h4>
                                                <small>
                                                  {getStatusBadge(
                                                    approver?.status,
                                                    approver?.reason,
                                                  )}
                                                </small>
                                              </span>
                                            </div>
                                          </div>
                                        }
                                      />
                                    </List.Item>
                                  );
                                }}
                              />
                            );
                          })()}
                        </Card>
                      </Col>

                      <Col className="approverCol" xs={24} md={12} xl={12}>
                        {/* Card for Admin Actions */}
                        <Card title="Admin Action Status" bordered={true}>
                          {expenseDetail.adminApprover &&
                          expenseDetail?.adminApprover?.length > 0 ? (
                            <List
                              itemLayout="horizontal"
                              dataSource={expenseDetail.adminApprover}
                              renderItem={(approver) => {
                                const adminApproverInfo = usersDetailList.find(
                                  (userItem) =>
                                    userItem.id === approver?.userID,
                                );

                                return (
                                  <List.Item>
                                    <List.Item.Meta
                                      title={
                                        <div className="spanApprovers">
                                          <span className="approvLabel">
                                            Approver Name:
                                          </span>

                                          <div className="imgTitle">
                                            {adminApproverInfo?.profilePicUrl ? (
                                              <img
                                                src={
                                                  process.env
                                                    .REACT_APP_BACKEND +
                                                  "/images/" +
                                                  adminApproverInfo.profilePicUrl
                                                }
                                                alt="profile"
                                              />
                                            ) : (
                                              <span className="initialLetter">
                                                {adminApproverInfo?.empName
                                                  ?.charAt(0)
                                                  ?.toUpperCase() || "?"}
                                              </span>
                                            )}

                                            <span>
                                              <h4>
                                                {adminApproverInfo?.empName ||
                                                  "N/A"}
                                              </h4>
                                              <small>
                                                {getStatusBadge(
                                                  approver?.status,
                                                  approver?.reason,
                                                )}
                                              </small>
                                            </span>
                                          </div>
                                        </div>
                                      }
                                    />
                                  </List.Item>
                                );
                              }}
                            />
                          ) : (
                            <div className="admin-status-awaiting">
                              <span>
                                Admin action will take place after all approvers
                                complete their approvals.
                              </span>
                            </div>
                          )}
                        </Card>
                      </Col>
                      {expenseDetail?.seekAcknowledgment?.[0]?.ackMessage !==
                        null && (
                        <Col sm={24}>
                          <Card
                            title="Acknowledgement Received"
                            bordered={true}
                            className="card-title-14"
                          >
                            {expenseDetail?.seekAcknowledgment?.[0]?.ackMessage}
                          </Card>
                        </Col>
                      )}

                      {/* {expenseDetail?.onBehalfApprovers && (
                    <Col sm={24}>
                      <Card
                        title="Approved by Admin on behalf of following approvers:"
                        bordered={true}
                        className="card-title-14"
                      >
                        {expenseDetail?.onBehalfApprovers}
                      </Card>
                    </Col>
                  )} */}

                      {expenseDetail?.history[expenseDetail?.history.length - 1]
                        .status ===
                        process.env.REACT_APP_SEEK_ACK_STATUS.toUpperCase().replace(
                          " ",
                          "",
                        ) &&
                      expenseDetail?.seekAcknowledgment?.[0]?.ackMessage ===
                        null &&
                      expenseDetail?.seekAcknowledgment?.[0]?.sentAt !== null &&
                      storeAcknowledgement !== false ? (
                        <Col sm={24}>
                          <Card
                            title="Seek Acknowledgement"
                            bordered={true}
                            className="card-title-14"
                          >
                            <span>Request Sent</span>
                          </Card>
                        </Col>
                      ) : (
                        storeRole === process.env.REACT_APP_AROLE &&
                        storeAcknowledgement === false &&
                        expenseDetail?.seekAcknowledgment?.[0]?.sentAt !==
                          null &&
                        expenseDetail?.seekAcknowledgment?.[0]?.ackMessage ===
                          null &&
                        expenseDetail?.history[
                          expenseDetail?.history.length - 1
                        ].status ===
                          process.env.REACT_APP_SEEK_ACK_STATUS.toUpperCase().replace(
                            " ",
                            "",
                          ) && (
                          <Col sm={24}>
                            <Card
                              title={
                                expenseDetail?.seekAcknowledgment?.[0]
                                  ?.ackMessage !== null
                                  ? "Acknowledgement Received"
                                  : "Seek Acknowledgement"
                              }
                              bordered={true}
                              className="card-title-14"
                            >
                              <div className="admin-status-awaiting">
                                <textarea
                                  name="ackMessage"
                                  className="form-control h-100px"
                                  onChange={(e) => {
                                    setExpenseDetail({
                                      ...expenseDetail,
                                      ackMessage: e?.target?.value
                                        ? e?.target?.value
                                        : "",
                                    });
                                  }}
                                />
                              </div>
                            </Card>
                          </Col>
                        )
                      )}
                    </Row>
                    {storeRole === process.env.REACT_APP_ADROLE &&
                      expenseDetail?.adminApprover?.[0]?.status ===
                        process.env.REACT_APP_APPROVED_STATUS.toUpperCase() && (
                        <div className="patmentWrapper">
                          <h3 className="expenseListTitle text-size-12">
                            Payment Status
                          </h3>

                          {expenseDetail?.paymentPaidOn === null && (
                            <>
                              <div className="checkboxWrapper">
                                <Row>
                                  <Col sm={12}>
                                    <label className="font-size-lg">
                                      <input
                                        type="checkbox"
                                        defaultValue={true}
                                        onChange={(e) => {
                                          setPaymentStatus(e.target.checked);

                                          if (e.target.checked === false) {
                                            setErrorMsg({
                                              ...errorMsg,
                                              paymentStatus:
                                                "Payment Status is Mandatory",
                                            });
                                          } else {
                                            const updatedErrorMsg = {
                                              ...errorMsg,
                                            };
                                            delete updatedErrorMsg.paymentStatus;
                                            delete updatedErrorMsg.paymentPaidOn;
                                            setErrorMsg(updatedErrorMsg);
                                          }
                                        }}
                                      />
                                      <span className="ml-2">
                                        Confirm Payment Status as Paid
                                      </span>
                                      <br />
                                      <small className="text-danger">
                                        {errorMsg.paymentStatus}
                                      </small>
                                    </label>
                                  </Col>
                                </Row>
                                {paymentStatus === true && (
                                  <div className="paymentWrapperInner">
                                    <Row>
                                      <Col sm={12}>
                                        <label className="font-size-lg">
                                          <label className="mb-3">
                                            <strong>Payment Paid On</strong>
                                          </label>

                                          <DatePicker
                                            // disabledDate={
                                            //   disableFutureDatesForPaymentPaidOn
                                            // }
                                            format="DD-MM-YYYY"
                                            placeholder="Select Date"
                                            className="form-control rounded-md d-inline-flex"
                                            allowClear={true}
                                            defaultValue={[
                                              expenseDetail?.paymentPaidOn
                                                ? dayjs(
                                                    expenseDetail?.paymentPaidOn,
                                                    "YYYY-MM-DD",
                                                  )
                                                : null,
                                            ]}
                                            onChange={(date, dateString) => {
                                              const formattedDate = moment(
                                                dateString,
                                                "DD-MM-YYYY",
                                              ).format("YYYY-MM-DD");
                                              setPaymentStatus(true);
                                              if (formattedDate) {
                                                setPaymentPaidOn(formattedDate);
                                              }

                                              if (date === "") {
                                                setErrorMsg({
                                                  ...errorMsg,
                                                  paymentPaidOn:
                                                    "Payment Paid On date is Mandatory",
                                                });
                                              } else {
                                                const updatedErrorMsg = {
                                                  ...errorMsg,
                                                };
                                                delete updatedErrorMsg.paymentPaidOn;
                                                setErrorMsg(updatedErrorMsg);
                                              }
                                            }}
                                          />

                                          <small className="text-danger">
                                            {errorMsg.paymentPaidOn}
                                          </small>
                                        </label>
                                      </Col>
                                      <Col sm={12}>
                                        <label className="font-size-lg">
                                          <label className="mb-3">
                                            Mode of Payment
                                          </label>
                                          <Radio.Group
                                            onChange={handleMainChange}
                                            value={
                                              mainSelectionForModeOfPayment
                                            }
                                            style={{ width: "100%" }}
                                          >
                                            <Row>
                                              <Col span={24}>
                                                <Radio value="HDFC Bank">
                                                  HDFC Bank
                                                </Radio>
                                              </Col>
                                              <Col span={24}>
                                                <Radio value="9071 GPAY">
                                                  9071 GPAY
                                                </Radio>
                                              </Col>
                                              <Col span={24}>
                                                <Radio value="Credit Card">
                                                  Credit Card
                                                </Radio>
                                              </Col>
                                              <Col span={24}>
                                                <Radio value="6449 Personal">
                                                  6449 Personal
                                                </Radio>
                                              </Col>
                                            </Row>
                                          </Radio.Group>
                                          {mainSelectionForModeOfPayment ===
                                            "Credit Card" && (
                                            <div
                                              style={{
                                                paddingLeft: 24,
                                                marginTop: 12,
                                              }}
                                            >
                                              <Radio.Group
                                                onChange={
                                                  handleCreditCardOptionChange
                                                }
                                                value={creditCardOption}
                                              >
                                                <Radio value="1421 Corporate">
                                                  1421 Corporate
                                                </Radio>
                                                <br />
                                                <Radio value="2463 Corporate">
                                                  2463 Corporate
                                                </Radio>
                                                <br />
                                                <Radio value="411009 Amex">
                                                  41009 Amex
                                                </Radio>
                                              </Radio.Group>
                                            </div>
                                          )}
                                          <small className="text-danger">
                                            {errorMsg.modeOfPayment}
                                          </small>
                                        </label>
                                      </Col>
                                    </Row>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          <div className="checkboxWrapper">
                            {expenseDetail?.paymentPaidOn !== null && (
                              <p className="expenseApproved">
                                Payment Paid On :{" "}
                                <strong>
                                  {moment(expenseDetail?.paymentPaidOn).format(
                                    "Do MMM, YYYY",
                                  )}
                                </strong>
                              </p>
                            )}
                            {expenseDetail?.modeOfPayment && (
                              <p className="expenseApproved">
                                Mode of Payment:{" "}
                                <strong>{expenseDetail?.modeOfPayment}</strong>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    <div className="text-center footerBtn">
                      <div className="footerReversment">
                        <small>Total Reimbursement</small>
                        <h4>
                          {process.env.REACT_APP_DEFAULT_CURRENCY}
                          {totalReimbursement !== null
                            ? totalReimbursement
                            : "--"}
                        </h4>
                      </div>

                      {shouldShowResubmitButton && (
                        <>
                          <div className="text-right mt-5">
                            <SubmitButton
                              // key="resubmit"
                              type="primary"
                              //  htmlType="submit"
                              submitName="Resubmit"
                              className="btn ml-auto d-block px-5 py-2 defaultbutton rounded-lg"
                              onClick={handleResubmitClick}
                            />
                            {/* <Button
                            type="primary"
                            htmlType="submit"
                            className=" btn font-size-h5 font-weight-bold mx-auto px-5 py-2 redbutton rounded-lg text-white"
                          >
                            Resubmit
                          </Button> */}
                          </div>
                        </>
                      )}
                      {shouldShowUpdateButton && (
                        <div className="text-right mt-5">
                          <SubmitButton
                            key="update"
                            type="primary"
                            htmlType="submit"
                            submitName="Update"
                            className="btn ml-auto d-block px-5 py-2 defaultbutton rounded-lg"
                          />
                          {/* <Button
                          type="primary"
                          htmlType="submit"
                          className=" btn font-size-h5 font-weight-bold mx-auto px-5 py-2 redbutton rounded-lg text-white"
                        >
                          Update
                        </Button> */}
                        </div>
                      )}
                      {expenseDetail?.status ===
                        process.env.REACT_APP_DRAFT_STATUS.toUpperCase() && (
                        <div className="text-right mt-5">
                          <SubmitButton
                            key="submit"
                            type="primary"
                            htmlType="submit"
                            submitName="Submit"
                            className="btn ml-auto d-block px-5 py-2 defaultbutton rounded-lg"
                          />
                          {/* <Button
                          type="primary"
                          htmlType="submit"
                          className=" btn font-size-h5 font-weight-bold mx-auto px-5 py-2 redbutton rounded-lg text-white"
                        >
                          Submit
                        </Button> */}
                        </div>
                      )}
                    </div>
                    <Modal
                      title="Confirm Expense"
                      open={isModalVisible}
                      onCancel={handleCancel}
                      className="custom-modal"
                      // cancelText="No"
                      // okText="Yes"
                      footer={[
                        <SubmitButton
                          key="cancel"
                          type="primary"
                          className="btn px-5 py-2 defaultWhiteBtn rounded-lg"
                          submitName="No"
                          onClick={handleCancel}
                        />,
                        <SubmitButton
                          key="submit"
                          type="primary"
                          htmlType="submit"
                          submitName="Yes"
                          className="btn px-5 py-2 defaultbutton rounded-lg"
                          onClick={handleOk}
                        />,
                      ]}
                    >
                      {(expenseDetail?.isNoResponse === true &&
                        expenseDetail?.history[
                          expenseDetail?.history.length - 1
                        ]?.status ===
                          process.env.REACT_APP_RESENT_AFTER_NORESPONSE) ||
                      (paymentStatus === true && paymentPaidOn !== null) ? (
                        <p>
                          Please review your selected or entered information
                          carefully before updating the form, as changes may not
                          be possible once it's updated.
                        </p>
                      ) : (
                        <p>
                          Please review your selected or entered information
                          carefully before resubmitting the form, as changes may
                          not be possible once it's resubmitted.
                        </p>
                      )}
                    </Modal>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
